import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { commercialError } from '../commercial/useCommercialApi';
import { buildDraftPatch, buildClonePayload, buildPublishPayload } from '../commercial/planVersionPayload';
import { usd, limitValue, primaryLabel, listFrom } from '../commercial/commercialFormat';
import { AlertTriangle, CheckCircle2, X, Copy, Save, UploadCloud, GitCompare } from 'lucide-react';

// GOV13-A4 — plan-COMPOSITION eligibility. FEATURE eligibility is BACKEND-AUTHORITATIVE: list_features projects
// `selectable_for_plan` + `disabled_reason` (killed/retired/unreleased/unspecified vs released-or-sold), and the
// UI simply consumes it — it never reconstructs release rules from raw fields, and defaults SAFE (disabled) if
// the projection is missing. Integrations remain selectable unless their release state was withdrawn. The
// backend validate/publish routes stay the final authority in every case.
const WITHDRAWN_INTEGRATION_STATES = new Set(['retired', 'deprecated', 'planned', 'in_development', 'internal_testing', 'private_beta']);
const featureEligible = (f) => f.selectable_for_plan === true;
const featureReason = (f) => f.disabled_reason || 'Not available for plan inclusion';
const integrationEligible = (it) => !WITHDRAWN_INTEGRATION_STATES.has(it.status || 'released');

/**
 * SACP — actionable Plan Version editor.
 * Versioned workflow: published version → CLONE to draft → EDIT → COMPARE → PUBLISH new immutable
 * version. A published version can NEVER be edited (backend enforces 409 PUBLISHED_VERSION_IMMUTABLE).
 * Publishing does NOT migrate existing tenants (no silent price change); FJON79W1 stays grandfathered.
 * Staging drafts are NOT synced to Stripe — no live Price/charge is created here.
 */
const LIMIT_FIELDS = [
  ['max_staff', 'Staff'], ['monthly_engagements', 'Engagements'],
  ['active_integrations', 'Integration slots'], ['monthly_ai_resolutions', 'AI resolutions'],
  ['storage_gb', 'Storage (GB)'],
  ['active_vehicles', 'Included active vehicles'], ['active_trackers', 'Included active trackers'],
];

const PlanVersionEditor = ({ planId, api, onClose, onChanged }) => {
  const [plan, setPlan] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [draft, setDraft] = useState(null);       // the version currently being edited (draft only)
  const [form, setForm] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  // GOV13-A4 — canonical feature/integration catalogues for per-plan composition (read from the LIVE owner
  // endpoints; never a hardcoded frontend catalogue). A transient read failure must NEVER clear selections.
  const [featCat, setFeatCat] = useState([]);
  const [intgCat, setIntgCat] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [catErr, setCatErr] = useState(null);
  // Modal state replaces the removed native prompt()/confirm() for every commercial mutation.
  const [modal, setModal] = useState(null);       // { kind:'clone'|'save'|'publish', version } | null
  const [mLabel, setMLabel] = useState('');       // clone: new draft label
  const [mReason, setMReason] = useState('');     // required reason for clone/save/publish
  const [mBusy, setMBusy] = useState(false);
  const [mErr, setMErr] = useState(null);
  const openModal = (kind, version) => { setModal({ kind, version }); setMLabel(kind === 'clone' ? 'v2.1-draft' : ''); setMReason(''); setMErr(null); };
  const closeModal = () => { setModal(null); setMLabel(''); setMReason(''); setMErr(null); setMBusy(false); };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const d = await api.get(`/api/v1/super-admin/commercial/plans/${encodeURIComponent(planId)}`);
      setPlan(d.data?.plan || null);
      setVersions(d.data?.versions || []);
    } catch (err) { setError(commercialError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [planId]);

  // Load the canonical catalogues once. Uses allSettled so one failing catalogue does not blank the other,
  // and — critically — a failure leaves any already-loaded selections in `form` untouched (no silent clear).
  const loadCatalogs = async () => {
    setCatLoading(true); setCatErr(null);
    const [f, i] = await Promise.allSettled([
      api.get('/api/v1/super-admin/commercial/features'),
      api.get('/api/v1/super-admin/commercial/integrations'),
    ]);
    const failed = [];
    if (f.status === 'fulfilled') setFeatCat(listFrom(f.value.data)); else failed.push('features');
    if (i.status === 'fulfilled') setIntgCat(listFrom(i.value.data)); else failed.push('integrations');
    if (failed.length) setCatErr(`Could not load the ${failed.join(' and ')} catalogue. Your current selections are preserved — retry to change them.`);
    setCatLoading(false);
  };
  useEffect(() => { loadCatalogs(); /* eslint-disable-next-line */ }, []);

  const toggleFeature = (key, on) => setForm((prev) => ({ ...prev, features: { ...(prev.features || {}), [key]: on } }));
  const toggleConnector = (key, on) => setForm((prev) => {
    const set = new Set(prev.connectors || []);
    if (on) set.add(key); else set.delete(key);
    return { ...prev, connectors: Array.from(set) };
  });

  const isPublished = (v) => v.state === 'active' || v.state === 'grandfathered' || v.immutable;

  const submitClone = async () => {
    if (!mLabel.trim()) { setMErr('A draft version label is required.'); return; }
    if (!mReason.trim()) { setMErr('A reason is required (recorded in the audit log).'); return; }
    setMBusy(true); setMErr(null);
    try {
      const v = modal.version;
      const r = await api.post(`/api/v1/super-admin/commercial/plans/${encodeURIComponent(planId)}/versions/${encodeURIComponent(v.id)}/clone`, buildClonePayload(mLabel, mReason));
      setToast({ ok: true, msg: `Draft ${r.data?.version?.version || mLabel.trim()} created from ${v.version}. Audit recorded.` });
      closeModal();
      await load();
      onChanged && onChanged();
    } catch (err) { setMErr(commercialError(err)); setMBusy(false); }
  };

  const editDraft = (v) => {
    setDraft(v);
    setForm({
      display_name: v.display_name ?? '',
      price_cents: v.price_cents ?? '',
      annual_price_cents: v.annual_price_cents ?? '',
      currency: v.currency ?? 'USD',
      included_ai_operations: v.included_credits?.ai_operations ?? '',
      included_communication: v.included_credits?.communication ?? '',
      limits: { ...(v.limits || {}) },
      // GOV13-A4 — initialize composition from the authoritative draft (never fabricated defaults).
      features: { ...(v.features || {}) },
      connectors: [...(v.connectors || [])],
    });
    setCompareResult(null);
  };

  const submitSave = async () => {
    if (!mReason.trim()) { setMErr('A reason is required (recorded in the audit log).'); return; }
    setMBusy(true); setMErr(null);
    try {
      const r = await api.patch(`/api/v1/super-admin/commercial/plans/${encodeURIComponent(planId)}/versions/${encodeURIComponent(draft.id)}`, buildDraftPatch(form, mReason));
      setToast({ ok: true, msg: 'Draft saved. Audit recorded.' });
      setDraft(r.data?.version || draft);
      closeModal();
      await load();
    } catch (err) { setMErr(commercialError(err)); setMBusy(false); }
  };

  const submitPublish = async () => {
    if (!mReason.trim()) { setMErr('A reason is required (recorded in the audit log).'); return; }
    setMBusy(true); setMErr(null);
    try {
      const r = await api.post(`/api/v1/super-admin/commercial/plans/${encodeURIComponent(planId)}/versions/${encodeURIComponent(draft.id)}/publish`, { reason: mReason.trim() });
      setToast({ ok: true, msg: `Published ${draft.version} (immutable, hash ${String(r.data?.content_hash || '').slice(0, 8)}). Existing tenants unchanged.` });
      closeModal();
      setDraft(null); setForm(null);
      await load(); onChanged && onChanged();
    } catch (err) { setMErr(commercialError(err)); setMBusy(false); }
  };

  // Price-change impact preview for the publish modal (read-only, real values).
  const publishImpact = () => {
    const active = versions.find((x) => isPublished(x));
    const oldCents = active?.price_cents;
    const newCents = form?.price_cents !== '' ? parseInt(form.price_cents, 10) : draft?.price_cents;
    const pct = (oldCents && newCents != null) ? Math.round(((newCents - oldCents) / oldCents) * 100) : null;
    return { oldCents, newCents, pct };
  };

  const compareWithActive = async (v) => {
    const active = versions.find((x) => isPublished(x) && x.id !== v.id);
    if (!active) { setToast({ ok: false, msg: 'No published version to compare against.' }); return; }
    try {
      const r = await api.get(`/api/v1/super-admin/commercial/plans/${encodeURIComponent(planId)}/versions/${encodeURIComponent(v.id)}/compare/${encodeURIComponent(active.id)}`);
      setCompareResult({ against: active.version, ...r.data });
    } catch (err) { setToast({ ok: false, msg: commercialError(err) }); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 overflow-y-auto p-4" role="dialog" aria-label="Plan version editor">
      <Card className="bg-white w-full max-w-3xl my-8 shadow-xl">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Plan Versions — {plan?.display_name || planId}</CardTitle>
          <button onClick={onClose} aria-label="Close editor"><X className="h-5 w-5 text-gray-500" /></button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
            Staging commercial draft — not synchronized with Stripe. Publishing creates a new immutable version and never migrates existing tenants or creates a charge.
          </div>
          {error && <div className="flex items-center gap-2 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-sm" role="alert"><AlertTriangle className="h-4 w-4" />{error}</div>}
          {toast && <div className={`flex items-center gap-2 p-2 rounded border text-sm ${toast.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`} role="status">{toast.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}{toast.msg}</div>}

          {loading ? <p className="text-sm text-gray-400">Loading…</p> : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>
                {['Version', 'State', 'Monthly', 'Slots', 'Actions'].map((h) => <th key={h} className="px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {versions.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-2 py-2 font-medium">{v.version}</td>
                    <td className="px-2 py-2">
                      <Badge className={isPublished(v) ? 'bg-green-100 text-green-800' : v.state === 'draft' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}>
                        {isPublished(v) ? 'Published · immutable' : v.state}
                      </Badge>
                    </td>
                    <td className="px-2 py-2">{v.price_cents != null ? `${usd(v.price_cents)}/mo` : (v.billing_class === 'custom' ? 'Custom' : 'Not configured')}</td>
                    <td className="px-2 py-2">{limitValue((v.limits || {}).active_integrations)}</td>
                    <td className="px-2 py-2 space-x-2 whitespace-nowrap">
                      {isPublished(v)
                        ? <button className="text-xs text-purple-700 underline inline-flex items-center gap-1" onClick={() => openModal('clone', v)}><Copy className="h-3 w-3" />Clone to draft</button>
                        : <>
                            <button className="text-xs text-blue-700 underline" onClick={() => editDraft(v)}>Edit</button>
                            <button className="text-xs text-gray-700 underline inline-flex items-center gap-1" onClick={() => compareWithActive(v)}><GitCompare className="h-3 w-3" />Compare</button>
                          </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}

          {compareResult && (
            <div className="text-xs border rounded p-3 bg-gray-50">
              <p className="font-semibold mb-1">Differences vs {compareResult.against}</p>
              {Object.keys(compareResult.differences || {}).length === 0
                ? <p className="text-gray-500">No differences.</p>
                : Object.entries(compareResult.differences).map(([f, d]) => (
                  <div key={f}>{f}: <span className="text-red-600">{JSON.stringify(d.b)}</span> → <span className="text-green-700">{JSON.stringify(d.a)}</span></div>
                ))}
            </div>
          )}

          {draft && form && (
            <div className="border rounded p-3 space-y-3">
              <p className="font-semibold text-sm">Editing draft: {draft.version}</p>
              <label className="text-xs text-gray-600 block">Version name
                <input type="text" className="mt-1 w-full border rounded px-2 py-1 text-sm" value={form.display_name}
                  aria-label="Plan version name"
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
                <span className="text-[10px] text-gray-400">Human-facing name for this version. The version identifier ({draft.version}) is immutable.</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-600">Monthly price (USD cents)
                  <input type="number" className="mt-1 w-full border rounded px-2 py-1 text-sm" value={form.price_cents}
                    onChange={(e) => setForm({ ...form, price_cents: e.target.value })} />
                  <span className="text-gray-400">= {form.price_cents !== '' ? usd(parseInt(form.price_cents, 10)) : '—'}/mo</span>
                </label>
                <label className="text-xs text-gray-600">Annual price (USD cents)
                  <input type="number" className="mt-1 w-full border rounded px-2 py-1 text-sm" value={form.annual_price_cents}
                    onChange={(e) => setForm({ ...form, annual_price_cents: e.target.value })} />
                </label>
                {LIMIT_FIELDS.map(([k, label]) => (
                  <label key={k} className="text-xs text-gray-600">{label}
                    <input type="number" className="mt-1 w-full border rounded px-2 py-1 text-sm" value={form.limits[k] ?? ''}
                      onChange={(e) => setForm({ ...form, limits: { ...form.limits, [k]: e.target.value } })} />
                  </label>
                ))}
              </div>

              {/* D18-C — currency + SEPARATE wallet credit allocations (never one combined field) */}
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">Currency &amp; included credits</p>
                <div className="grid grid-cols-3 gap-3">
                  <label className="text-xs text-gray-600">Currency (ISO)
                    <select className="mt-1 w-full border rounded px-2 py-1 text-sm" value={form.currency}
                      onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                      <option value="USD">USD</option>
                      <option value="CAD">CAD (schema — unpublished)</option>
                    </select>
                  </label>
                  <label className="text-xs text-gray-600">AI &amp; Operations credits / month
                    <input type="number" min="0" className="mt-1 w-full border rounded px-2 py-1 text-sm" value={form.included_ai_operations}
                      onChange={(e) => setForm({ ...form, included_ai_operations: e.target.value })} />
                  </label>
                  <label className="text-xs text-gray-600">Communication credits / month
                    <input type="number" min="0" className="mt-1 w-full border rounded px-2 py-1 text-sm" value={form.included_communication}
                      onChange={(e) => setForm({ ...form, included_communication: e.target.value })} />
                  </label>
                </div>
                {(form.included_ai_operations !== '' && form.included_communication !== '') && (
                  <p className="text-xs text-gray-400 mt-1">Summary total: {(parseInt(form.included_ai_operations, 10) || 0) + (parseInt(form.included_communication, 10) || 0)} credits
                    ({form.included_ai_operations} AI/Ops + {form.included_communication} Communication)</p>
                )}
                <p className="text-xs text-gray-500 mt-2">AI Voice: 3 credits per minute, billed in 20-second increments (Communication wallet).
                  A successful AI Voice Resolution is a separate 10-credit charge (AI/Operations wallet) — both disclosed, never a combined hidden charge.</p>
              </div>

              {/* GOV13-A4 — per-plan FEATURE composition (canonical Feature Catalog; backend is final authority). */}
              {(() => { const canEdit = draft?.allowed_actions?.edit_draft !== false; return (
              <div className="border-t pt-3" data-testid="feature-composition">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-700">Plan features</p>
                  {catErr && <button type="button" className="text-xs text-blue-700 underline" onClick={loadCatalogs}>Retry</button>}
                </div>
                {catErr && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-2" role="alert">{catErr}</div>}
                {catLoading ? <p className="text-xs text-gray-400">Loading catalogue…</p>
                  : featCat.length === 0 ? (
                    Object.keys(form.features || {}).filter((k) => form.features[k]).length === 0
                      ? <p className="text-xs text-gray-500">No features selected.</p>
                      : <p className="text-xs text-gray-600">Current selections preserved: {Object.keys(form.features).filter((k) => form.features[k]).join(', ')}</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto">
                      {featCat.map((f) => {
                        const key = f.feature_key;
                        const elig = featureEligible(f);
                        const checked = form.features?.[key] === true;
                        const disabled = !canEdit || (!elig && !checked);
                        return (
                          <label key={key} className={`flex items-start gap-2 text-xs p-1.5 rounded border ${checked ? 'border-green-300 bg-green-50' : 'border-gray-100'} ${disabled ? 'opacity-60' : ''}`}>
                            <input type="checkbox" className="mt-0.5" checked={checked} disabled={disabled}
                              aria-label={`Include feature ${f.display_name || key}`}
                              onChange={(e) => toggleFeature(key, e.target.checked)} />
                            <span>
                              <span className="font-medium text-gray-800">{primaryLabel(f.display_name, key)}</span>
                              <span className="block text-[10px] text-gray-400 font-mono">{key}</span>
                              {f.description && <span className="block text-[10px] text-gray-500">{f.description}</span>}
                              {!elig && <span className="block text-[10px] text-red-600">{featureReason(f)}</span>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
              </div>
              ); })()}

              {/* GOV13-A4 — per-plan INTEGRATION composition (canonical Integration Catalog; plan-level inclusion only). */}
              {(() => { const canEdit = draft?.allowed_actions?.edit_draft !== false; return (
              <div className="border-t pt-3" data-testid="integration-composition">
                <p className="text-xs font-semibold text-gray-700 mb-2">Plan integrations</p>
                {catLoading ? <p className="text-xs text-gray-400">Loading catalogue…</p>
                  : intgCat.length === 0 ? (
                    (form.connectors || []).length === 0
                      ? <p className="text-xs text-gray-500">No integrations selected.</p>
                      : <p className="text-xs text-gray-600">Current selections preserved: {(form.connectors || []).join(', ')}</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto">
                      {intgCat.map((it) => {
                        const key = it.integration_key;
                        const elig = integrationEligible(it);
                        const checked = (form.connectors || []).includes(key);
                        const disabled = !canEdit || (!elig && !checked);
                        return (
                          <label key={key} className={`flex items-start gap-2 text-xs p-1.5 rounded border ${checked ? 'border-green-300 bg-green-50' : 'border-gray-100'} ${disabled ? 'opacity-60' : ''}`}>
                            <input type="checkbox" className="mt-0.5" checked={checked} disabled={disabled}
                              aria-label={`Include integration ${it.display_name || key}`}
                              onChange={(e) => toggleConnector(key, e.target.checked)} />
                            <span>
                              <span className="font-medium text-gray-800">{primaryLabel(it.display_name, key)}</span>
                              <span className="block text-[10px] text-gray-400 font-mono">{key}</span>
                              <span className="block text-[10px] text-gray-500">{it.provider || '—'}{it.pricing_class ? ` · ${it.pricing_class}` : ''}{it.counts_toward_active_slots ? ' · uses a slot' : ''}</span>
                              {!elig && <span className="block text-[10px] text-red-600">Not available for plan inclusion ({it.status || 'unspecified'})</span>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                <p className="text-[10px] text-gray-400 mt-1">Plan-level inclusion only — tenant credentials and setup are never configured here.</p>
              </div>
              ); })()}

              <div className="flex gap-2">
                <Button size="sm" onClick={() => openModal('save', draft)}><Save className="h-3 w-3 mr-1" />Save draft</Button>
                <Button size="sm" variant="outline" onClick={() => openModal('publish', draft)}><UploadCloud className="h-3 w-3 mr-1" />Publish new version</Button>
                <Button size="sm" variant="ghost" onClick={() => { setDraft(null); setForm(null); }}>Cancel</Button>
              </div>
              <p className="text-xs text-gray-400">-1 in an allowance means Unlimited. Prices are integer USD cents. Publishing is immutable and never migrates existing tenants.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Commercial-mutation modals (replace native prompt()/confirm()). Reason required everywhere. */}
      <Dialog open={!!modal} onOpenChange={(o) => { if (!o) closeModal(); }}>
        <DialogContent className="sm:max-w-md">
          {modal?.kind === 'clone' && (
            <>
              <DialogHeader>
                <DialogTitle>Clone to a new draft</DialogTitle>
                <DialogDescription>From published version <span className="font-medium">{modal.version?.version}</span>. The draft is fully editable; the published version stays immutable.</DialogDescription>
              </DialogHeader>
              <label className="block text-xs font-semibold text-gray-600 mb-1 mt-2">New draft label <span className="text-red-500">*</span></label>
              <input className="w-full border rounded px-2 py-1.5 text-sm" value={mLabel} onChange={(e) => setMLabel(e.target.value)} placeholder="v2.1-draft" />
            </>
          )}
          {modal?.kind === 'save' && (
            <DialogHeader>
              <DialogTitle>Save draft changes</DialogTitle>
              <DialogDescription>Saving updates the draft <span className="font-medium">{modal.version?.version}</span> only. No tenant is affected and no charge is created.</DialogDescription>
            </DialogHeader>
          )}
          {modal?.kind === 'publish' && (() => {
            const { oldCents, newCents, pct } = publishImpact();
            return (
              <>
                <DialogHeader>
                  <DialogTitle>Publish {modal.version?.version} as immutable</DialogTitle>
                  <DialogDescription>A published version can never be edited again.</DialogDescription>
                </DialogHeader>
                <div className="rounded border bg-gray-50 px-3 py-2 text-sm mt-2 space-y-1">
                  <div className="flex justify-between"><span className="text-gray-500">Current price</span><span className="font-medium">{oldCents != null ? `${usd(oldCents)}/mo` : '—'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">New price</span><span className="font-medium">{newCents != null ? `${usd(newCents)}/mo` : '—'}{pct != null ? ` (${pct >= 0 ? '+' : ''}${pct}%)` : ''}</span></div>
                </div>
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                  Existing tenants are <span className="font-semibold">not migrated</span> — FJON79W1 stays Grandfathered on its current version. No Stripe Price or charge is created (staging).
                </div>
              </>
            );
          })()}

          <label className="block text-xs font-semibold text-gray-600 mb-1 mt-2">Reason (recorded in audit) <span className="text-red-500">*</span></label>
          <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={mReason} onChange={(e) => setMReason(e.target.value)} placeholder="Why is this change being made?" />

          {mErr && <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mt-2"><AlertTriangle className="h-4 w-4" />{mErr}</div>}

          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={closeModal} disabled={mBusy}>Cancel</Button>
            <Button
              onClick={modal?.kind === 'clone' ? submitClone : modal?.kind === 'save' ? submitSave : submitPublish}
              disabled={mBusy}>
              {mBusy ? 'Working…' : modal?.kind === 'clone' ? 'Create draft' : modal?.kind === 'save' ? 'Save draft' : 'Publish version'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlanVersionEditor;
